import type { ParameterInfo } from '@/types/operationParameter';

interface OrderableField {
  name: string;
  required?: boolean;
  _parameterInfo?: ParameterInfo;
  _parentGroup?: { key: string };
}

function dependencyReferences(info: ParameterInfo['info']): string[] {
  const configurations = [
    info.dynamicValues,
    info.dynamicSchema,
    info.dynamicTree?.open,
    info.dynamicTree?.browse,
  ];
  return [
    ...(info.inputDependencies ?? []).map(dependency => dependency.parameterReference),
    ...(info.dependencies?.parameters ?? []).map(dependency => dependency.parameterReference),
    ...(info.dependencies?.values ?? []).map(dependency => dependency.path),
    ...configurations.flatMap(config =>
      Object.values(config?.parameters ?? {}).flatMap(parameter =>
        parameter.parameterReference ? [parameter.parameterReference] : [],
      ),
    ),
    ...(info.clientDynamicValues?.request.bindings ?? []).map(binding => binding.parameterReference),
    ...(info.generatedByParameter ? [info.generatedByParameter] : []),
  ];
}

/**
 * Required-first presentation within existing sibling runs. Groups stay in
 * place; dependency spans keep their source order, even when requiredness
 * conflicts. This never changes visibility, metadata, or serialized values.
 */
export function orderParameterFields<T extends OrderableField>(fields: readonly T[]): T[] {
  const positions = new Map<string, number[]>();
  fields.forEach((field, index) => {
    const info = field._parameterInfo;
    for (const key of new Set([field.name, info?.id, info?.parameterKey, info?.parameterName, info?.alternativeKey])) {
      if (key) positions.set(key, [...(positions.get(key) ?? []), index]);
    }
  });

  const pinned = new Set<number>();
  fields.forEach((field, index) => {
    const info = field._parameterInfo?.info;
    if (!info) return;
    for (const reference of dependencyReferences(info)) {
      // A hidden prerequisite or one in another section still anchors its
      // dependent here. Ambiguous names preserve every matching span.
      pinned.add(index);
      for (const prerequisite of positions.get(reference) ?? []) {
        for (let i = Math.min(index, prerequisite); i <= Math.max(index, prerequisite); i++) {
          pinned.add(i);
        }
      }
    }
  });

  const ordered: T[] = [];
  let start = 0;
  while (start < fields.length) {
    const first = fields[start]!;
    if (pinned.has(start)) {
      ordered.push(first);
      start++;
      continue;
    }
    const group = first._parentGroup?.key;
    let end = start + 1;
    while (end < fields.length && !pinned.has(end) && fields[end]!._parentGroup?.key === group) {
      end++;
    }
    const siblings = fields.slice(start, end);
    ordered.push(...siblings.filter(field => field.required), ...siblings.filter(field => !field.required));
    start = end;
  }
  return ordered;
}
